import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, query, where, collection, getDocs } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false); // New state to track if the user is an admin

  useEffect(() => {
    // Check for user data in localStorage on initial load
    const localUserData = localStorage.getItem('userData');
    if (localUserData) {
      const parsedData = JSON.parse(localUserData);
      setUserData(parsedData);
      setIsLoggedIn(true);
      setIsAdmin(parsedData.loginCode === 'GPA10'); // Check if the user is admin
      setLoading(false); // Set loading to false after reading from localStorage
    } else {
      // Listen for authentication state changes
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            const docRef = doc(db, "users", user.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              const data = docSnap.data();
              setUserData(data);
              setIsAdmin(data.loginCode === 'GPA10'); // Check if the user is admin
              localStorage.setItem('userData', JSON.stringify(data));
              setIsLoggedIn(true);
            }
          } catch (error) {
            console.error("Error fetching user data: ", error);
          }
        } else {
          setIsLoggedIn(false);
          setUserData(null);
          setIsAdmin(false);
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('userData');
        }
        setLoading(false);
      });

      return () => unsubscribe(); // Clean up the listener
    }
  }, []);

  const login = async (username, password) => {
    const q = query(collection(db, "users"), where("username", "==", username));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();

      if (userData.password === password) {
        setIsLoggedIn(true);
        setUserData(userData);
        setIsAdmin(userData.loginCode === 'GPA10'); // Check if the user is admin
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('userData', JSON.stringify(userData)); // Store user data in local storage
        localStorage.setItem("username", username);
        return userData; // Return userData after successful login
      } else {
        throw new Error('Incorrect password');
      }
    } else {
      throw new Error('Username does not exist');
    }
  };

  const logout = () => {
    setIsLoggedIn(false);
    setUserData(null);
    setIsAdmin(false);
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userData'); // Clear user data from local storage
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, userData, isAdmin, login, logout, loading }}>
      {!loading && children} {/* Render children only after loading is false */}
    </AuthContext.Provider>
  );
};
